package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Injects the dark "command center" stylesheet (qp- prefix) for the platform
 * owner's dashboard. Kept separate from the light venue-admin CSS; fonts are
 * loaded by [InjectAdminStyles], which the platform screen also calls.
 */
@Composable
internal fun InjectPlatformStyles() {
    LaunchedEffect(Unit) {
        val style =
            document.getElementById("qk-platform-styles") ?: document.createElement("style").also {
                it.setAttribute("id", "qk-platform-styles")
                document.head?.appendChild(it)
            }
        style.textContent = PLATFORM_CSS
    }
}

private const val PLATFORM_CSS = """
/* ---------- shell ---------- */
.qp-root{position:relative;min-height:100vh;background:#050E0A;color:#F2F7F4;font-family:system-ui,-apple-system,'Segoe UI',sans-serif}
.qp-root *{box-sizing:border-box}
.qp-display{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-0.02em}
.qp-scene{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
.qp-scene i{position:absolute;border-radius:50%;filter:blur(8px)}
.qp-scene i:nth-of-type(1){left:-320px;top:-280px;width:720px;height:720px;background:radial-gradient(circle,rgba(15,157,110,.20) 0%,rgba(15,157,110,.08) 40%,transparent 68%)}
.qp-scene i:nth-of-type(2){right:-300px;top:60px;width:600px;height:600px;background:radial-gradient(circle,rgba(126,226,188,.12) 0%,rgba(126,226,188,.05) 42%,transparent 68%)}
.qp-scene i:nth-of-type(3){left:30vw;bottom:-360px;width:640px;height:640px;background:radial-gradient(circle,rgba(232,163,61,.08) 0%,transparent 66%)}
.qp-grid{position:absolute;inset:-60px 0;background-image:linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px);background-size:58px 58px;mask-image:radial-gradient(90% 70% at 50% 30%,#000 30%,transparent 100%);-webkit-mask-image:radial-gradient(90% 70% at 50% 30%,#000 30%,transparent 100%)}
.qp-noise{position:fixed;inset:0;z-index:1;pointer-events:none;opacity:.35;background-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")}

.qp-shell{position:relative;z-index:2;display:flex;min-height:100vh}
.qp-side{position:fixed;left:0;top:0;bottom:0;width:72px;z-index:20;display:flex;flex-direction:column;align-items:center;gap:6px;padding:16px 0;background:rgba(8,20,15,.72);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-right:1px solid rgba(255,255,255,.07)}
.qp-side-logo{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:linear-gradient(150deg,#0F9D6E,#34D399);color:#04140D;font-weight:800;font-family:'Unbounded',sans-serif;font-size:18px;margin-bottom:10px}
.qp-side-spring{flex:1}
.qp-side-item{position:relative;width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;color:rgba(242,247,244,.55);cursor:pointer;transition:background .18s,color .18s;user-select:none}
.qp-side-item:hover{background:rgba(255,255,255,.06);color:#F2F7F4}
.qp-side-item.on{background:rgba(52,211,153,.16);color:#7EE2BC}
.qp-side-item.on::before{content:"";position:absolute;left:-16px;top:50%;transform:translateY(-50%);width:3px;height:22px;border-radius:2px;background:#34D399}
.qp-side-item .qp-tip{position:absolute;left:56px;white-space:nowrap;background:rgba(10,22,18,.96);border:1px solid rgba(255,255,255,.1);color:#F2F7F4;font-size:12px;font-weight:600;padding:6px 10px;border-radius:9px;opacity:0;transform:translateX(-4px);pointer-events:none;transition:opacity .16s,transform .16s;box-shadow:0 12px 30px rgba(0,0,0,.4);z-index:30}
.qp-side-item:hover .qp-tip{opacity:1;transform:translateX(0)}
.qp-main{flex:1;margin-left:72px;padding:26px 30px 60px;max-width:1352px}

/* ---------- primitives ---------- */
.qp-card{position:relative;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:20px;padding:18px 20px;margin-bottom:14px;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px)}
.qp-card-title{font-size:11px;text-transform:uppercase;letter-spacing:.14em;color:rgba(242,247,244,.5);font-weight:700;margin-bottom:14px}
.qp-kpi{position:relative;flex:1;min-width:158px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:18px;padding:15px 16px;display:flex;align-items:center;gap:12px;overflow:hidden}
.qp-kpi-icon{width:42px;height:42px;min-width:42px;border-radius:12px;display:flex;align-items:center;justify-content:center}
.qp-kpi-value{font-family:'Unbounded',sans-serif;font-weight:700;font-size:clamp(18px,4.2vw,26px);line-height:1.05;white-space:nowrap;letter-spacing:-0.02em}
.qp-kpi-label{font-size:11.5px;color:rgba(242,247,244,.55);margin-top:2px}

.qp-chip{padding:7px 13px;border-radius:999px;font-size:12px;font-weight:700;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.09);color:rgba(242,247,244,.62);cursor:pointer;transition:background .18s,color .18s,border-color .18s;user-select:none;white-space:nowrap}
.qp-chip:hover{color:#F2F7F4;border-color:rgba(255,255,255,.2)}
.qp-chip.on{background:rgba(52,211,153,.18);border-color:rgba(52,211,153,.5);color:#7EE2BC}

.qp-pulse{position:relative}
.qp-pulse::after{content:"";position:absolute;inset:0;border-radius:50%;animation:qp-pulse 1.7s ease-in-out infinite}
@keyframes qp-pulse{0%,100%{box-shadow:0 0 0 0 rgba(52,211,153,.5)}50%{box-shadow:0 0 0 7px rgba(52,211,153,0)}}

/* ranked bars */
.qp-rank{position:relative;width:100%;border-radius:10px;background:rgba(255,255,255,.04);overflow:hidden}
.qp-rank-fill{position:absolute;left:0;top:0;bottom:0;background:rgba(52,211,153,.16);border-radius:10px}
.qp-rank-row{position:relative;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:8px 11px}

/* tables */
.qp-th{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:rgba(242,247,244,.45)}
.qp-trow{border-bottom:1px solid rgba(255,255,255,.06)}
.qp-trow:hover{background:rgba(255,255,255,.03)}

/* skeleton */
.qp-skeleton{background:linear-gradient(90deg,rgba(255,255,255,.045) 25%,rgba(255,255,255,.09) 50%,rgba(255,255,255,.045) 75%);background-size:800px 100%;animation:qp-shimmer 1.3s linear infinite;border-radius:16px}
@keyframes qp-shimmer{0%{background-position:-800px 0}100%{background-position:800px 0}}

/* charts */
.qp-chart{position:relative;width:100%}
.qp-chart svg{display:block;width:100%;height:auto;overflow:visible}
.qp-tip-float{position:absolute;pointer-events:none;background:rgba(10,22,18,.97);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:7px 10px;font-size:11.5px;line-height:1.45;box-shadow:0 14px 34px rgba(0,0,0,.5);white-space:nowrap;transform:translate(-50%,-115%);opacity:0;transition:opacity .12s;z-index:6}
.qp-hit{cursor:crosshair}

/* map */
.qp-map svg{display:block;width:100%;height:auto}
.qp-map path{fill:rgba(255,255,255,.06);stroke:rgba(5,14,10,.9);stroke-width:.4;transition:fill .2s}
.qp-map path:hover{fill:#34D399!important}

/* session step chips */
.qp-step{display:inline-flex;align-items:center;gap:5px;padding:5px 9px;border-radius:8px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.08);font-size:11.5px;color:rgba(242,247,244,.82);white-space:nowrap}
.qp-step.cta{background:rgba(52,211,153,.14);border-color:rgba(52,211,153,.4);color:#7EE2BC}
.qp-step-arrow{color:rgba(242,247,244,.3);font-size:12px}

/* funnel */
.qp-funnel-bar{height:44px;border-radius:12px;background:linear-gradient(90deg,rgba(15,157,110,.85),rgba(52,211,153,.7));display:flex;align-items:center;padding:0 14px;min-width:64px;transition:width .5s cubic-bezier(.22,.61,.36,1)}

@media(max-width:760px){
  .qp-side{flex-direction:row;top:auto;bottom:0;right:0;width:100%;height:64px;padding:0 8px;gap:2px;border-right:none;border-top:1px solid rgba(255,255,255,.08);justify-content:space-around}
  .qp-side-logo{display:none}
  .qp-side-spring{display:none}
  .qp-side-item .qp-tip{display:none}
  .qp-main{margin-left:0;padding:20px 16px 88px;max-width:100%}
}
"""
